import React from 'react';
import { Navigate, useParams } from 'react-router';
import { Box, CircularProgress } from '@mui/material';

import { useStoryblokMode } from '../auth/useStoryblokMode';
import { useCurrentUser } from '../auth/useCurrentUser';
import { paths } from './paths';

/**
 * Content-surface guard for a route's `storyblok` metadata (CPT-1219).
 *
 * Each tenant exposes exactly one content-management surface: the Storyblok
 * Content tab when the tenant's Storyblok mode is on, the legacy CMS tab when
 * it is off. The sidebar already hides the other one; this closes the
 * direct-URL / deep-link gap so a hidden surface cannot be reached by typing
 * its path.
 *
 * Deliberately NOT an RBAC check. Content and CMS have no gating permission —
 * the tenant attribute is the entire mechanism (see the divergent-gating note
 * on CPT-1219). Do not add a `permission` to these routes; it would introduce a
 * second, competing source of visibility.
 *
 * Presentation-only: redirecting away from a hidden surface never mutates
 * content, cohort, or CMS data. Flipping the attribute back restores the tab
 * and its data unchanged.
 *
 * CPT-1375 — `flexSurface`: a route that is ALSO listed under Flex Components
 * (`sidebar.alsoIn.ignoreStoryblokGate`) is not only a content surface, so the
 * gate alone must not decide it. FAQs, Cards and Agreements have to be
 * reachable under Flex whether the Storyblok setting is on or off (AC5/AC6) —
 * that is the whole point: a tenant live on Legacy Content can build a Flex
 * renewal without switching its live content over. Such a route is allowed when
 * the tenant is Storyblok-only OR the viewer can see Flex, mirroring the
 * `alsoIn` placement in the sidebar so nav and guard cannot disagree.
 *
 * RBAC is unchanged and still applies: `RequirePermission` wraps this guard, so
 * the route's own `permission` is enforced first either way.
 *
 * While the mode is resolving we render a spinner rather than guessing. A
 * wrong guess would either flash a surface the tenant should not author in, or
 * bounce a legitimate visitor off the page they asked for — and both surfaces'
 * pages fire their own data queries on mount.
 */
const Spinner: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

const RequireStoryblokMode: React.FC<{
  storyblok?: 'only' | 'legacy';
  /** Route is also a Flex surface — see the `flexSurface` note above. */
  flexSurface?: boolean;
  children: React.ReactNode;
}> = ({ storyblok, flexSurface, children }) => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const { storyblokOnly, ready } = useStoryblokMode(tenantCode ?? '');
  const { isSuperAdmin, hasRole, hasPermission, rbacV2Enabled, isLoading } = useCurrentUser();

  // Ungated route — the common case.
  if (!storyblok) return <>{children}</>;

  if (!ready) return <Spinner />;
  // Same reasoning as RequirePermission's loading guard: deciding before
  // /api/me resolves would bounce a legitimate Flex viewer off their own page.
  if (flexSurface && isLoading) return <Spinner />;

  // Mirrors how the Flex group itself is gated (role 'flex' pre-RBAC_V2,
  // permission 'flex_components' after) so the nav entry and this guard agree.
  const canSeeFlex = flexSurface
    ? (rbacV2Enabled ? hasPermission('flex_components') : (isSuperAdmin || hasRole('flex')))
    : false;

  const allowed = storyblok === 'only' ? (storyblokOnly || canSeeFlex) : !storyblokOnly;
  if (allowed) return <>{children}</>;

  // Send the user to the surface their tenant actually has, so the redirect is
  // useful rather than a dead end. `useStoryblokMode` fails open to
  // storyblokOnly=false, so an errored lookup lands on the legacy CMS — the
  // pre-Storyblok default, and never a blank screen.
  const target = tenantCode
    ? storyblokOnly
      ? paths.storyblokCmsComponents(tenantCode)
      : paths.cmsComponents(tenantCode)
    : '/';
  return <Navigate to={target} replace />;
};

export default RequireStoryblokMode;
